import DiscardModal, {
  type DiscardModalCopy,
} from '@/client/components/admin/overlay/DiscardModal';
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

interface FormNavigationBlockerProps {
  enabled: boolean;
  /** Overrides the discard modal's generic copy. */
  discardCopy?: DiscardModalCopy;
}

/**
 * Discard-changes guard for forms with unsaved work. Covers both ways
 * a user can lose state:
 *
 *   1. **React Router navigation** — `useBlocker` intercepts in-app
 *      route changes and pops the custom `<DiscardModal>` (matches
 *      the rest of our admin UX).
 *
 *   2. **Window unload** — `beforeunload` covers everything outside
 *      React Router: tab close, browser refresh, deep-link to an
 *      external URL, and both reloads in `utils/reloadForNewBuild`.
 *      The browser's native "Changes you may have made…" confirm
 *      dialog isn't as pretty as our modal, but it's the only thing
 *      that runs in those contexts.
 *
 * The two arms can both fire for one navigation. The build-id reload
 * runs when the router commits, which is before React unmounts this
 * route, so a user who picks Discard in arm 1 can still meet the
 * native dialog from arm 2. Narrow — it needs a deploy mid-session
 * and a dirty form — and the second prompt is redundant rather than
 * destructive, since they have already chosen to discard.
 *
 * Both arms gate on the same `enabled` flag (typically `!canLeave`
 * or a form's `isDirty`) so the consumer has a single switch.
 *
 * Without the `beforeunload` arm, `window.location.reload()`
 * silently discards unsaved work — relevant because the app reloads
 * itself after a deploy. With it, the user gets the native prompt
 * before the reload runs.
 */
export default function FormNavigationBlocker({
  enabled,
  discardCopy,
}: FormNavigationBlockerProps) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      enabled && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [enabled]);

  return (
    <DiscardModal
      isOpen={blocker.state === 'blocked'}
      onClose={() => blocker.reset?.()}
      onDiscard={() => blocker.proceed?.()}
      copy={discardCopy}
    />
  );
}
